import React, { useState, useEffect } from 'react';
import { CardElement, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import donationAPI, { APIErrorHandler } from '../config/api-config';

const SimplePaymentForm = ({
  selectedAmount,
  isMonthly,
  customerData,
  onBack,
  onClose,
  onSuccess,
  onPaymentAttempt
}) => {
  const stripe = useStripe();
  const elements = useElements();
  
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [cardErrors, setCardErrors] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  // Détecter si on est sur mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 🎯 FONCTION PRINCIPALE DE TRAITEMENT DU PAIEMENT
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validation initiale
    if (!stripe || !elements) {
      setError('Stripe not loaded. Please refresh the page.');
      return;
    }

    // Vérifier les champs requis via la fonction parente
    if (onPaymentAttempt && !onPaymentAttempt()) {
      setError('Please fill in all required fields before proceeding.');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      console.log('🚀 Starting payment process...');

      // Utiliser l'élément approprié selon le device
      const cardElement = isMobile 
        ? elements.getElement(CardNumberElement)
        : elements.getElement(CardElement);

      // Créer le payment method
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: `${customerData.firstName} ${customerData.lastName}`,
          email: customerData.email,
          address: {
            line1: customerData.streetAddress,
            line2: customerData.addressLine2 || null,
            city: customerData.city,
            postal_code: customerData.zipCode,
            state: customerData.state,
            country: customerData.country,
          },
        },
      });

      if (paymentMethodError) {
        console.error('❌ Payment method creation failed:', paymentMethodError);
        setError(paymentMethodError.message);
        setProcessing(false);
        return;
      }

      console.log('✅ Payment method created:', paymentMethod.id);

      // 💳 APPELER VOTRE BACKEND POUR CRÉER LE PAYMENT INTENT
      const paymentIntentResponse = await donationAPI.createCardPaymentIntent(
        selectedAmount,
        customerData,
        isMonthly
      );

      console.log('✅ Payment intent received from backend');

      // Confirmer le paiement avec Stripe
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        paymentIntentResponse.client_secret,
        {
          payment_method: paymentMethod.id,
        }
      );

      if (confirmError) {
        console.error('❌ Payment confirmation failed:', confirmError);
        setError(confirmError.message);
        setProcessing(false);
        return;
      }

      // 🎉 PAIEMENT RÉUSSI
      console.log('🎉 Payment successful!', paymentIntent.id);
      setPaymentSuccess(true);
      setProcessing(false);

      // Appeler le callback de succès
      if (onSuccess) {
        onSuccess({
          paymentMethod: 'card',
          paymentIntent: paymentIntent,
          client_secret: paymentIntentResponse.client_secret,
          payment_intent_id: paymentIntentResponse.payment_intent_id,
          amount: selectedAmount,
          isMonthly: isMonthly
        });
      }

    } catch (error) {
      console.error('❌ Payment processing error:', error);
      
      // Utiliser le gestionnaire d'erreurs pour obtenir un message convivial
      const userFriendlyMessage = APIErrorHandler.getErrorMessage(error);
      setError(userFriendlyMessage);
      
      // Logger l'erreur pour le debugging
      APIErrorHandler.logError(error, {
        amount: selectedAmount,
        isMonthly: isMonthly,
        customerEmail: customerData.email
      });
      
      setProcessing(false);
    }
  };

  // Gestion des erreurs de champ individuels
  const handleCardFieldChange = (fieldName) => (event) => {
    setCardErrors(prev => ({
      ...prev,
      [fieldName]: event.error ? event.error.message : null
    }));
    
    // Effacer l'erreur générale si un champ est corrigé
    if (!event.error && error) {
      setError(null);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        color: '#374151',
        fontFamily: '"Rubik", system-ui, sans-serif',
        fontSize: '14px',
        lineHeight: '265px',
        '::placeholder': {
          color: '#9CA3AF',
        },
        iconColor: '#666EE8',
      },
      invalid: {
        color: '#EF4444',
        iconColor: '#EF4444',
      },
      complete: {
        color: '#059669',
        iconColor: '#059669',
        showIcon: true,
      },
    },
    // 🎯 REMOVE POSTAL CODE
    hidePostalCode: true,
  };

  // 🎉 SUCCESS DISPLAY
  if (paymentSuccess) {
    return (
      <div className="payment-success">
        <div className="success-icon">✅</div>
        <h3>Thank you for your donation!</h3>
        <p>Your {isMonthly ? 'monthly' : 'one-time'} donation of ${selectedAmount} has been processed successfully.</p>
        <p>You will receive a confirmation email shortly.</p>
        {isMonthly && (
          <p><strong>Monthly donations:</strong> Your card will be charged ${selectedAmount} every month. You can cancel anytime by contacting us.</p>
        )}
      </div>
    );
  }

  return (
    <div className="payment-form">
      <div className="payment-summary">
        <h4>Payment Summary</h4>
        <div className="summary-row">
          <span>Amount:</span>
          <span>${selectedAmount}</span>
        </div>
        <div className="summary-row">
          <span>Type:</span>
          <span>{isMonthly ? 'Monthly Donation' : 'One-time Donation'}</span>
        </div>
        {isMonthly && (
          <div className="recurring-info">
            <small>Your card will be charged ${selectedAmount} every month</small>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Version Desktop - Un seul champ CardElement */}
        {!isMobile && (
          <div className="card-element-container">
            <label htmlFor="card-element">
              Card Information *
            </label>
            <div className="card-element-wrapper">
              <CardElement
                id="card-element"
                options={cardElementOptions}
                onChange={(event) => {
                  if (event.error) {
                    setError(event.error.message);
                  } else {
                    setError(null);
                  }
                }}
              />
            </div>
          </div>
        )}

        {/* Version Mobile - Champs séparés */}
        {isMobile && (
          <>
            {/* Numéro de carte - Ligne 1 */}
            <div className="card-field-container">
              <label htmlFor="card-number">
                Card Number *
              </label>
              <div className="card-field-wrapper">
                <CardNumberElement
                  id="card-number"
                  options={cardElementOptions}
                  onChange={handleCardFieldChange('cardNumber')}
                />
              </div>
              {cardErrors.cardNumber && (
                <div className="field-error">{cardErrors.cardNumber}</div>
              )}
            </div>

            {/* Expiration et CVC - Ligne 2 */}
            <div className="card-row">
              <div className="card-field-container">
                <label htmlFor="card-expiry">
                  MM/YY *
                </label>
                <div className="card-field-wrapper">
                  <CardExpiryElement
                    id="card-expiry"
                    options={cardElementOptions}
                    onChange={handleCardFieldChange('cardExpiry')}
                  />
                </div>
                {cardErrors.cardExpiry && (
                  <div className="field-error">{cardErrors.cardExpiry}</div>
                )}
              </div>

              <div className="card-field-container">
                <label htmlFor="card-cvc">
                  CVC *
                </label>
                <div className="card-field-wrapper">
                  <CardCvcElement
                    id="card-cvc"
                    options={cardElementOptions}
                    onChange={handleCardFieldChange('cardCvc')}
                  />
                </div>
                {cardErrors.cardCvc && (
                  <div className="field-error">{cardErrors.cardCvc}</div>
                )}
              </div>
            </div>
          </>
        )}

        {error && (
          <div className="payment-error">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <div className="payment-actions">
          <button
            type="submit"
            className="btn-primary payment-submit"
            disabled={!stripe || processing}
          >
            {processing ? (
              <>
                <span className="spinner"></span>
                Processing...
              </>
            ) : (
              <>
                🔒 Donate ${selectedAmount} {isMonthly ? '/month' : ''}
              </>
            )}
          </button>
        </div>
      </form>

      <div className="payment-security">
        <p>
          <span className="security-icon">🔒</span>
          Your payment information is secure and encrypted
        </p>
        <p>
          <small>
            Powered by Stripe • No card details are stored on our servers
          </small>
        </p>
      </div>
    </div>
  );
};

export default SimplePaymentForm;